import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Music } from 'lucide-react';
import { PublicLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { getBlogPost } from '@/lib/api';

// Font style mappings
const FONT_CLASSES = {
  default: 'font-sans',
  serif: 'font-display',
  handwriting: 'font-cursive'
};

// Simple markdown-like rendering with font support
const renderContent = (content, fontClass = '') => {
  return content.split('\n').map((line, index) => {
    const trimmedLine = line.trim();
    if (!trimmedLine) return <br key={index} />;
    
    // Headings (check trimmed line)
    if (trimmedLine.startsWith('## ')) {
      const text = trimmedLine.substring(3);
      return <h2 key={index} className={`text-xl font-bold text-charcoal mb-3 ${fontClass}`}>{text}</h2>;
    }
    if (trimmedLine.startsWith('# ')) {
      const text = trimmedLine.substring(2);
      return <h1 key={index} className={`text-2xl font-bold text-charcoal mb-4 ${fontClass}`}>{text}</h1>;
    }
    
    // Process inline formatting
    let rendered = trimmedLine;
    
    // Bold: **text**
    rendered = rendered.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // Italic: *text* (but not **text**)
    rendered = rendered.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>');
    // Links: [text](url)
    rendered = rendered.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-terracotta underline">$1</a>');
    
    // List items
    if (trimmedLine.startsWith('- ')) {
      rendered = '• ' + rendered.substring(2);
      return (
        <p 
          key={index} 
          className={`text-stone leading-relaxed mb-2 pl-4 ${fontClass}`}
          dangerouslySetInnerHTML={{ __html: rendered }}
        />
      );
    }
    
    return (
      <p 
        key={index} 
        className={`text-stone leading-relaxed mb-4 ${fontClass}`}
        dangerouslySetInnerHTML={{ __html: rendered }}
      />
    );
  });
};

const BlogPostPage = () => {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPost = async () => {
      try {
        const res = await getBlogPost(postId);
        setPost(res.data);
      } catch (err) {
        setError('Article non trouvé');
      } finally {
        setLoading(false);
      }
    };
    loadPost();
  }, [postId]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-pulse text-stone">Chargement...</div>
        </div>
      </PublicLayout>
    );
  }

  if (error || !post) {
    return (
      <PublicLayout>
        <div className="min-h-screen flex flex-col items-center justify-center">
          <p className="text-stone text-lg mb-4">{error || 'Article non trouvé'}</p>
          <Link to="/actualites">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour aux actualités
            </Button>
          </Link>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      {/* Hero with image */}
      {post.image_url && (
        <section className="relative h-64 md:h-96 overflow-hidden">
          <img
            src={post.image_url}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 to-transparent"></div>
        </section>
      )}

      {/* Article content */}
      <section className={`section-padding bg-white ${!post.image_url ? 'pt-24' : ''}`}>
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Back link */}
            <Link 
              to="/actualites"
              className="inline-flex items-center gap-2 text-terracotta hover:text-terracotta-dark transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour aux actualités
            </Link>

            {/* Date */}
            <div className="flex items-center gap-2 text-sm text-stone mb-4">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(post.created_at)}</span>
              {post.updated_at && post.updated_at !== post.created_at && (
                <span className="text-stone/60">(mis à jour le {formatDate(post.updated_at)})</span>
              )}
            </div>

            {/* Title */}
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl text-charcoal mb-8">
              {post.title}
            </h1>

            {/* Audio player if available */}
            {post.audio_url && (
              <div className="mb-8 p-4 bg-terracotta/5 rounded-xl border border-terracotta/20">
                <div className="flex items-center gap-2 mb-3">
                  <Music className="w-5 h-5 text-terracotta" />
                  <span className="text-charcoal font-medium">Écoutez cet article</span>
                </div>
                <audio controls className="w-full">
                  <source src={post.audio_url} />
                  Votre navigateur ne supporte pas la lecture audio.
                </audio>
              </div>
            )}

            {/* Content */}
            <div className="prose prose-lg prose-stone max-w-none">
              {renderContent(post.content, FONT_CLASSES[post.font_style] || '')}
            </div>
          </motion.div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default BlogPostPage;
